import { useContext, useEffect, useRef } from "react";
import { isRight } from "fp-ts/Either";
import { cargoEquipmentChangedT } from "dora-contracts";
import { SocketContext } from "../../../../SocketConnection";

/**
 * Tells the equipment picker when someone else (another dispatcher, the public
 * API, a driver, or a save that started a rental) changed the cargo's
 * equipment, so it reloads it. Joins the cargo's equipment room while mounted.
 *
 * `cargoId` is null on an order not created yet: there is nothing to follow.
 */
export const useCargoEquipmentLiveUpdates = (
  cargoId: string | null,
  onChanged: () => void
) => {
  const socket = useContext(SocketContext);
  // Read when a change arrives, so a new callback doesn't rejoin the room.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useEffect(() => {
    if (!socket || !cargoId) {
      return;
    }
    const join = () => socket.emit("join-cargo-equipment-room", { cargoId });
    const onEquipmentChanged = (payload: unknown) => {
      const decoded = cargoEquipmentChangedT.decode(payload);
      if (isRight(decoded) && decoded.right.cargoId === cargoId) {
        onChangedRef.current();
      }
    };
    // A reconnect gets a new socket on the server, without its rooms, and may
    // have missed changes.
    const onReconnect = () => {
      join();
      onChangedRef.current();
    };
    join();
    socket.on("cargo-equipment.changed", onEquipmentChanged);
    socket.on("connect", onReconnect);
    return () => {
      socket.off("cargo-equipment.changed", onEquipmentChanged);
      socket.off("connect", onReconnect);
      socket.emit("leave-cargo-equipment-room", { cargoId });
    };
  }, [socket, cargoId]);
};
